import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../application/account/AuthProvider';
import { useTranslate } from '../../application/i18n/LocaleProvider';
import { CloseIcon } from '../icons/ControlIcons';

/**
 * The account, and the one place this product ever asks a reader for one
 * (SPEC-009 PR-9.5, D-8).
 *
 * A real `<dialog>` opened with `showModal()`, the same as `GarageFilterSheet`:
 * focus trapping, Escape, the top layer and an inert background are four things
 * the platform gets right and a hand-rolled bottom sheet gets subtly wrong.
 *
 * THE CONSENT SENTENCE COMES BEFORE THE BUTTONS, and it is ordinary body copy
 * rather than small print under them. What signing in does here is publish the
 * reader's provider display name — often their real name — on a board other
 * people can read. That is the entire reason there is no handle to type
 * (SPEC-009 D-3), and a reader cannot agree to it after they have already
 * pressed the button. It is the first thing in the sheet after the title.
 *
 * The sheet is reached from the end of a run and nowhere else. It is a consent
 * step, not a destination — an account tab would be a tab most readers never
 * press, in a product whose whole tab bar is two entries wide.
 */
export default function AccountSheet({ onClose }) {
    const t = useTranslate();
    const {
        emailLinkState,
        isAuthenticated,
        sendEmailLink,
        signIn,
        signOut,
        user,
    } = useAuth();
    const dialogRef = useRef(null);
    const [email, setEmail] = useState('');

    // Opening is a one-off, and deliberately its own effect: an effect that
    // depended on `onClose` would close and re-open the dialog whenever the
    // parent re-rendered, and the second `showModal()` throws.
    useEffect(() => {
        const dialog = dialogRef.current;

        dialog.showModal();

        return () => {
            if (dialog.open) {
                dialog.close();
            }
        };
    }, []);

    // Signing in is the reason the sheet is open, so finishing it closes the
    // sheet rather than leaving the reader to dismiss a panel whose job is done.
    // Signing OUT does not: the reader is looking at the control they just used
    // and closing under them would read as a bug.
    useEffect(() => {
        if (isAuthenticated) {
            onClose();
        }
    }, [isAuthenticated, onClose]);

    const isSending = emailLinkState.status === 'sending';
    const isSent = emailLinkState.status === 'sent';

    function onSubmitEmail(event) {
        event.preventDefault();
        sendEmailLink(email);
    }

    return (
        <dialog className="account-sheet" ref={dialogRef} onCancel={onClose} onClose={onClose}>
            <div className="account-sheet__head">
                <h2 className="account-sheet__title">{t('account.title')}</h2>

                <button type="button" className="account-sheet__close" onClick={onClose}>
                    <CloseIcon size={20} aria-hidden="true" />
                    <span className="visually-hidden">{t('account.close')}</span>
                </button>
            </div>

            {isAuthenticated ? (
                <div className="account-sheet__body">
                    <p className="account-sheet__signed-in">{t('account.signedInAs', { name: user.name })}</p>

                    <button type="button" className="button button--quiet" onClick={signOut}>
                        {t('account.signOut')}
                    </button>
                </div>
            ) : (
                <div className="account-sheet__body">
                    {/* Before any button, and in body copy. See the note above. */}
                    <p className="account-sheet__consent">{t('account.consent')}</p>

                    <button type="button" className="button button--primary account-sheet__provider" onClick={signIn}>
                        {t('account.google')}
                    </button>

                    {/* One tenant across three products (ADR-0003), which is
                        worth saying: a reader with a habb.me account is being
                        asked to sign in, not to sign up. */}
                    <p className="account-sheet__siblings">{t('account.siblings')}</p>

                    <form className="account-sheet__email" onSubmit={onSubmitEmail}>
                        <label className="account-sheet__label" htmlFor="account-email">
                            {t('account.emailLabel')}
                        </label>

                        <div className="account-sheet__row">
                            <input
                                className="account-sheet__input"
                                id="account-email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                inputMode="email"
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                                placeholder={t('account.emailPlaceholder')}
                            />

                            <button type="submit" className="button account-sheet__send" disabled={isSending}>
                                {t(isSending ? 'account.emailSending' : 'account.emailSend')}
                            </button>
                        </div>

                        {/* Announced when it changes: the reader has just pressed
                            a button whose whole result is this sentence, and on a
                            failure there is nothing else on screen that moved. */}
                        <p className="account-sheet__status" role="status">
                            {isSent ? t('account.emailSent', { email: emailLinkState.pendingEmail }) : null}
                            {emailLinkState.errorKey ? t(emailLinkState.errorKey) : null}
                        </p>
                    </form>
                </div>
            )}
        </dialog>
    );
}
